import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Calendar, Plus, Edit, Trash2, Clock, MapPin, ArrowLeft } from 'lucide-react';
import { getGoogleMapsEmbedUrl, getGoogleStaticMapUrl } from '@/lib/config';
import { useNavigate } from 'react-router-dom';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import MobileCalendar from '@/features/shared/components/MobileCalendar';
import MobileAddGigModal from '@/features/shared/components/MobileAddGigModal';
import { normalizeTime24, formatGigTime12h } from '@/lib/gigUtils';

// ✅ IMPORT THE NEW FIREBASE AUTH CONTEXT AND SERVICES
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { db, functions } from '@/lib/firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

const CalendarPage = () => {
    // ✅ USEAUTH NOW PULLS FROM FIREBASE
    const { user } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();
    const mobile = useMobile();
    const [gigs, setGigs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
    const [editingGig, setEditingGig] = useState(null);
    const [formData, setFormData] = useState({ 
        date: '', 
        venue_name: '', 
        time: '', 
        ampm: 'PM',
        address: '', 
        postcode: '', 
        show_map: true 
    });

    // Helper function to convert DD/MM/YYYY to YYYY-MM-DD for HTML date input
    const convertToDateInputFormat = (ddmmyyyy) => {
        if (!ddmmyyyy) return '';
        const parts = ddmmyyyy.split('/');
        if (parts.length === 3) {
            const [day, month, year] = parts;
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        }
        return ddmmyyyy;
    };

    // Helper function to convert YYYY-MM-DD to DD/MM/YYYY for display
    const convertToDisplayFormat = (yyyymmdd) => {
        if (!yyyymmdd) return '';
        const parts = yyyymmdd.split('-');
        if (parts.length === 3) {
            const [year, month, day] = parts;
            return `${day}/${month}/${year}`;
        }
        return yyyymmdd;
    };

    // Custom date input handler
    const handleDateInputChange = (e) => {
        const value = e.target.value;
        // Allow only numbers and forward slashes
        const cleaned = value.replace(/[^0-9/]/g, '');
        
        // Auto-insert slashes
        let formatted = cleaned;
        if (cleaned.length >= 2 && !cleaned.includes('/')) {
            formatted = cleaned.slice(0, 2) + '/' + cleaned.slice(2);
        }
        if (formatted.length >= 5 && formatted.split('/').length === 2) {
            formatted = formatted.slice(0, 5) + '/' + formatted.slice(5);
        }
        
        // Limit to DD/MM/YYYY format
        if (formatted.length <= 10) {
            setFormData(prev => ({ ...prev, date: formatted }));
        }
    };

    // ✅ REFACTORED: Load gigs from Firestore
    const loadGigs = useCallback(async () => {
        if (user) {
            setLoading(true);
            try {
                const gigsQuery = query(
                    collection(db, 'gigs'),
                    where('user_id', '==', user.uid)
                    // Removed orderBy to avoid composite index requirement
                );
                const querySnapshot = await getDocs(gigsQuery);
                const gigsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // Sort in memory instead of requiring a composite index
                const sortedGigs = gigsData.sort((a, b) => {
                    const dateA = new Date(a.date);
                    const dateB = new Date(b.date);
                    return dateB - dateA; // Descending order
                });
                setGigs(sortedGigs);
            } catch (error) {
                console.error("Error loading gigs:", error);
                toast({ title: "Error loading gigs", description: error.message, variant: "destructive" });
            } finally {
                setLoading(false);
            }
        }
    }, [user, toast]);

    useEffect(() => { loadGigs(); }, [loadGigs]);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const openDialog = (gig = null) => {
        if (gig) {
            setEditingGig(gig);
            
            setFormData({
                date: convertToDisplayFormat(gig.date),
                venue_name: gig.venue_name,
                time: normalizeTime24(gig.time),
                ampm: 'PM',
                address: gig.address || '',
                postcode: gig.postcode || '',
                show_map: gig.show_map
            });
        } else {
            setEditingGig(null);
            setFormData({ 
                date: '', 
                venue_name: '', 
                time: '', 
                ampm: 'PM',
                address: '', 
                postcode: '', 
                show_map: true 
            });
        }
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        setIsDialogOpen(false);
        setEditingGig(null);
        setFormData({ 
            date: '', 
            venue_name: '', 
            time: '', 
            ampm: 'PM',
            address: '', 
            postcode: '', 
            show_map: true 
        });
    };

    // ✅ REFACTORED: Handle form submission to Firestore and Cloud Function
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.date || !formData.venue_name || !formData.time) {
            toast({ title: "Error", description: "Please fill in all required fields.", variant: "destructive" });
            return;
        }

        const time24Hour = normalizeTime24(formData.time);
        if (!time24Hour) {
            toast({ title: "Error", description: "Please enter a valid time.", variant: "destructive" });
            return;
        }

        const basePayload = {
            venue_name: formData.venue_name,
            date: convertToDateInputFormat(formData.date),
            time: time24Hour,
            address: formData.address || '',
            postcode: formData.postcode || '',
            show_map: formData.show_map !== false,
            user_id: user.uid,
        };

        let gigData = editingGig
            ? { ...basePayload, updated_at: serverTimestamp() }
            : { ...basePayload, created_at: serverTimestamp() };

        if (formData.postcode || formData.address) {
            try {
                // This calls a backend Cloud Function you need to create in Firebase.
                const geocode = httpsCallable(functions, 'geocode');
                const result = await geocode({ address: formData.address, postcode: formData.postcode });
                if (result.data.lat && result.data.lon) {
                    gigData.lat = result.data.lat;
                    gigData.lon = result.data.lon;
                }
            } catch (error) {
                console.error('Geocoding error:', error);
                toast({ title: "Geocoding Warning", description: "Could not find coordinates for the address." });
            }
        }

        try {
            if (editingGig) {
                const gigRef = doc(db, 'gigs', editingGig.id);
                await updateDoc(gigRef, gigData);
                toast({ title: "Gig updated!" });
            } else {
                await addDoc(collection(db, 'gigs'), gigData);
                toast({ title: "Gig added!" });
            }
            loadGigs();
            closeDialog();
        } catch (error) {
            console.error("Error saving gig:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        }
    };

    const handleEdit = (gig) => {
        if (mobile.isMobile) {
            setEditingGig(gig);
            setIsMobileModalOpen(true);
        } else {
            openDialog(gig);
        }
    };

    // Handle save for mobile modal
    const handleSaveGig = async (gigData) => {
        if (!gigData.title || !gigData.date) {
            toast({ title: "Error", description: "Please fill in all required fields.", variant: "destructive" });
            return;
        }

        let dataToSave = {
            venue_name: gigData.title,
            date: gigData.date,
            time: normalizeTime24(gigData.time || '') || '',
            address: gigData.venue || '',
            postcode: '',
            user_id: user.uid,
            created_at: serverTimestamp()
        };

        try {
            if (editingGig) {
                const gigRef = doc(db, 'gigs', editingGig.id);
                await updateDoc(gigRef, dataToSave);
                toast({ title: "Gig updated!" });
            } else {
                await addDoc(collection(db, 'gigs'), dataToSave);
                toast({ title: "Gig added!" });
            }
            loadGigs();
            setEditingGig(null);
        } catch (error) {
            console.error("Error saving gig:", error);
            toast({ title: "Error", description: error.message, variant: "destructive" });
        }
    };

    // ✅ REFACTORED: Handle deletion in Firestore
    const handleDelete = async (gigId) => {
        if (window.confirm('Are you sure you want to delete this gig?')) {
            try {
                const gigRef = doc(db, 'gigs', gigId);
                await deleteDoc(gigRef);
                toast({ title: "Gig deleted!" });
                loadGigs();
            } catch (error) {
                console.error("Error deleting gig:", error);
                toast({ title: "Error", description: error.message, variant: "destructive" });
            }
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
        });
    };

    const isUpcoming = (dateString) => {
        const gigDate = new Date(dateString);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return gigDate >= today;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet><title>Calendar Management - Your Song Request</title></Helmet>
            
            {/* Mobile-optimized Calendar */}
            {mobile.isMobile && (
                <MobileCalendar
                    events={gigs}
                    venues={[]}
                    onAddEvent={() => setIsMobileModalOpen(true)}
                    onEditEvent={handleEdit}
                    onDeleteEvent={handleDelete}
                    onAddVenue={() => setIsMobileModalOpen(true)}
                />
            )}
            
            {/* Desktop Calendar */}
            {!mobile.isMobile && (
                <main className="w-full h-full p-6">
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                        {/* Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex flex-col space-y-1">
                                <h1 className="text-2xl font-bold text-white">Calendar Management</h1>
                                <p className="text-sm text-gray-400">Show the world where you're gigging! Add an upcoming gig, to be displayed on our Homepage!</p>
                            </div>
                            <Button onClick={() => openDialog()} className="bg-blue-600 hover:bg-blue-700">
                                <Plus className="w-4 h-4 mr-2" />
                                Add Gig
                            </Button>
                        </div>

                    {/* Stats */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardContent className="p-4">
                                <div className="flex items-center space-x-2">
                                    <Calendar className="w-5 h-5 text-blue-400" />
                                    <span className="text-white font-semibold">{gigs.length}</span>
                                    <span className="text-gray-400">Total Gigs</span>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardContent className="p-4">
                                <div className="flex items-center space-x-2">
                                    <Clock className="w-5 h-5 text-green-400" />
                                    <span className="text-white font-semibold">
                                        {gigs.filter(gig => isUpcoming(gig.date)).length}
                                    </span>
                                    <span className="text-gray-400">Upcoming Gigs</span>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-gray-800/50 border-gray-700">
                            <CardContent className="p-4">
                                <div className="flex items-center space-x-2">
                                    <MapPin className="w-5 h-5 text-yellow-400" />
                                    <span className="text-white font-semibold">
                                        {gigs.filter(gig => gig.address || gig.postcode).length}
                                    </span>
                                    <span className="text-gray-400">With Location</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Gigs List */}
                    <div className="space-y-4">
                        {gigs.length === 0 ? (
                            <Card className="bg-gray-800/50 border-gray-700">
                                <CardContent className="p-8 text-center">
                                    <Calendar className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                                    <h3 className="text-xl font-semibold text-white mb-2">No Gigs Scheduled</h3>
                                    <p className="text-gray-400 mb-4">Start planning your performances by adding your first gig.</p>
                                    <Button onClick={() => openDialog()} className="bg-blue-600 hover:bg-blue-700">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Add Your First Gig
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            gigs.map((gig, i) => (
                                <GigCard
                                    key={gig.id}
                                    gig={gig}
                                    i={i}
                                    handleEdit={handleEdit}
                                    handleDelete={handleDelete}
                                    formatDate={formatDate}
                                    isUpcoming={isUpcoming(gig.date)}
                                />
                            ))
                        )}
                    </div>
                </motion.div>
            </main>
            )}

            {/* Mobile Add/Edit Gig Modal */}
            <MobileAddGigModal
                isOpen={mobile.isMobile && isMobileModalOpen}
                onClose={() => {
                    setIsMobileModalOpen(false);
                    setEditingGig(null);
                }}
                onSave={handleSaveGig}
                editingGig={editingGig}
            />

            {/* Add/Edit Gig Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingGig ? 'Edit Gig' : 'Add New Gig'}</DialogTitle>
                        <DialogDescription>
                            {editingGig ? 'Update your gig details' : 'Schedule a new performance'}
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="date">Date (DD/MM/YYYY) *</Label>
                                <Input
                                    id="date"
                                    name="date"
                                    type="text"
                                    value={formData.date}
                                    onChange={handleDateInputChange}
                                    placeholder="DD/MM/YYYY"
                                    required
                                    className="bg-gray-800 border-gray-600 text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="time">Time *</Label>
                                <Input
                                    id="time"
                                    name="time"
                                    type="time"
                                    value={formData.time}
                                    onChange={handleInputChange}
                                    required
                                    className="bg-gray-800 border-gray-600 text-white"
                                />
                                <p className="text-xs text-gray-500">24-hour picker — stored and shown consistently everywhere.</p>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="venue_name">Venue Name *</Label>
                            <Input
                                id="venue_name"
                                name="venue_name"
                                value={formData.venue_name}
                                onChange={handleInputChange}
                                placeholder="Enter venue name"
                                required
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="address">Address</Label>
                            <Input
                                id="address"
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                placeholder="Enter full address"
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="postcode">Postcode</Label>
                            <Input
                                id="postcode"
                                name="postcode"
                                value={formData.postcode}
                                onChange={handleInputChange}
                                placeholder="Enter postcode"
                                className="bg-gray-800 border-gray-600 text-white"
                            />
                        </div>
                        <div className="flex items-center space-x-2">
                            <Switch
                                id="show_map"
                                name="show_map"
                                checked={formData.show_map}
                                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, show_map: checked }))}
                            />
                            <Label htmlFor="show_map">Show map on public page</Label>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={closeDialog}>
                                Cancel
                            </Button>
                            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                                {editingGig ? 'Update Gig' : 'Add Gig'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
};

const GigCard = ({ gig, i, handleEdit, handleDelete, formatDate, isUpcoming }) => {
    return (
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.4, delay: i * 0.1 }}
        >
            <Card className={`border ${isUpcoming ? 'border-green-600 bg-green-900/20' : 'border-gray-700 bg-gray-800/50'}`}>
                <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                        <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                                <Calendar className={`w-5 h-5 ${isUpcoming ? 'text-green-400' : 'text-gray-400'}`} />
                                <h3 className="text-xl font-semibold text-white">{gig.venue_name}</h3>
                                {isUpcoming && (
                                    <span className="px-2 py-1 bg-green-600/20 text-green-300 text-xs rounded-full">
                                        Upcoming
                                    </span>
                                )}
                            </div>
                            <p className="text-gray-300 mb-2">{formatDate(gig.date)}</p>
                            <div className="flex items-center space-x-4 text-sm text-gray-400">
                                <div className="flex items-center space-x-1">
                                    <Clock className="w-4 h-4" />
                                    <span>{formatGigTime12h(gig.time)}</span>
                                </div>
                                {(gig.address || gig.postcode) && (
                                    <div className="flex items-center space-x-1">
                                        <MapPin className="w-4 h-4" />
                                        <span>{gig.address} {gig.postcode}</span>
                                    </div>
                                )}
                            </div>
                            {gig.show_map && gig.lat && gig.lon && (
                                <div className="mt-4">
                                    <img
                                        src={getGoogleStaticMapUrl(gig.lat, gig.lon, 15, '300x150')}
                                        alt="Venue location"
                                        className="w-full h-32 object-cover rounded-lg border border-gray-600"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="flex items-center space-x-2 ml-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(gig)}
                                className="text-blue-400 hover:text-blue-300"
                            >
                                <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(gig.id)}
                                className="text-red-400 hover:text-red-300"
                            >
                                <Trash2 className="w-4 h-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
};

export default CalendarPage;